<template>
  <div class="modal-overlay cp-overlay" @click.self="$emit('close')">
    <div class="cp" role="dialog" aria-modal="true" aria-label="Pick a class">
      <button class="modal-close" @click="$emit('close')" aria-label="Close">✕</button>
      <div class="cp-title">{{ heading }} <span class="cp-sub">— click to pick</span></div>
      <input ref="search" v-model="q" class="cp-search" type="text" placeholder="Filter…">
      <div class="cp-grid">
        <button v-for="c in filtered" :key="c.val || c.name" class="cp-tile" :class="{ on: (c.val || c.name) === current, mon: c.isMonster, special: c.special }" @click="$emit('pick', c.val || c.name)" :title="c.title || c.name">
          <span v-if="c.badge" class="cp-tile-badge" :title="c.badgeTitle || ''">{{ c.badge }}</span>
          <img v-if="c.icon" class="cp-tile-img" :src="c.icon" :alt="c.name" @error="$event.target.style.visibility = 'hidden'">
          <span v-else-if="c.emoji" class="cp-tile-emoji">{{ c.emoji }}</span>
          <span class="cp-tile-name">{{ c.name }}</span>
          <span v-if="c.n != null" class="cp-tile-n">🏆 {{ c.n }}</span>
          <span v-if="c.genders && c.genders.length" class="cp-tile-g">
            <span v-for="g in c.genders" :key="g" role="button" tabindex="0" class="cp-g"
                  :class="{ on: (c.val || c.name) === current && g === currentGender }" :title="c.name + ' ' + (g === 'Male' ? '♂ male' : '♀ female')"
                  @click.stop="$emit('pick', c.val || c.name, g)" @keydown.enter.stop.prevent="$emit('pick', c.val || c.name, g)">{{ g === 'Male' ? '♂' : '♀' }}</span>
          </span>
        </button>
      </div>
      <div v-if="!filtered.length" class="cp-empty">No classes match "{{ q }}".</div>
    </div>
  </div>
</template>

<script>
import modalA11y from '../mixins/modalA11y';

export default {
  name: 'ClassPickerModal',
  mixins: [modalA11y],
  props: {
    // [{ name, n?, titles?, isMonster?, icon?, val?, emoji?, special?, title?, badge?, badgeTitle?, genders? }],
    // pre-sorted. val overrides the emitted pick value, emoji is a sprite-less fallback,
    // badge is a corner mark (e.g. ✦ for a bonus class). genders (['Male', 'Female']) adds
    // ♂/♀ buttons that emit pick(value, gender); the tile itself emits pick(value).
    classes: { type: Array, default: () => [] },
    current: { type: String, default: '' },
    currentGender: { type: String, default: '' },  // highlights the current tile's ♂/♀
    heading: { type: String, default: '🏆 Champions by class' },
  },
  data() { return { q: '' }; },
  computed: {
    filtered() { const q = this.q.trim().toLowerCase(); return q ? this.classes.filter((c) => c.name.toLowerCase().includes(q)) : this.classes; },
  },
  mounted() { this.$nextTick(() => { if (this.$refs.search) this.$refs.search.focus(); }); },
};
</script>

<style scoped>
.cp { position: relative; width: 100%; max-width: 700px; max-height: 84vh; display: flex; flex-direction: column; background: var(--navy2); border: 1px solid var(--brass-edge); border-radius: 10px; padding: 16px 18px 18px; color: var(--ink-hi); box-shadow: 0 12px 44px rgba(0, 0, 0, 0.6); text-align: left; }
.cp-title { font-size: 16px; margin-bottom: 10px; }
.cp-sub { font-size: 11px; opacity: 0.55; font-weight: normal; }
.cp-search { width: 100%; box-sizing: border-box; margin-bottom: 12px; background: rgba(18, 24, 38, 0.6); color: #fff; border: 1px solid var(--brass-edge); border-radius: 5px; font-size: 13px; padding: 6px 10px; }
.cp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(78px, 1fr)); gap: 7px; overflow-y: auto; }
.cp-tile { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 6px 3px 5px; background: rgba(255, 255, 255, 0.04); border: 1px solid transparent; border-radius: 6px; cursor: pointer; color: var(--ink); }
.cp-tile:hover { background: rgba(217, 180, 94, 0.35); }
.cp-tile.on { border-color: var(--brass-hi); background: rgba(255, 216, 119, 0.12); }
.cp-tile.mon { background: rgba(150, 90, 160, 0.1); }
.cp-tile.mon:hover { background: rgba(150, 90, 160, 0.32); }
.cp-tile-img { width: 40px; height: 40px; object-fit: contain; image-rendering: pixelated; }
.cp-tile-emoji { height: 40px; font-size: 32px; line-height: 40px; }
.cp-tile.special { background: rgba(255, 216, 119, 0.08); border-color: rgba(255, 216, 119, 0.35); }
.cp-tile.special:hover { background: rgba(255, 216, 119, 0.18); }
.cp-tile.special.on { border-color: var(--brass-hi); }
.cp-tile { position: relative; }
.cp-tile-badge { position: absolute; top: 3px; right: 5px; font-size: 11px; color: var(--brass-hi); line-height: 1; }
.cp-tile-g { display: flex; gap: 4px; margin-top: 2px; }
.cp-g { padding: 0 7px; border-radius: 8px; font-size: 11px; line-height: 16px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.2); cursor: pointer; }
.cp-g:hover { background: rgba(217, 180, 94, 0.6); border-color: var(--brass-hi); }
.cp-g.on { background: rgba(217, 180, 94, 0.22); border-color: var(--brass-hi); color: #fff; font-weight: bold; }
.cp-tile-name { font-size: 10px; text-align: center; line-height: 1.1; max-width: 100%; }
.cp-tile-n { font-size: 10px; font-weight: bold; color: var(--brass); white-space: nowrap; }
.cp-tile.on .cp-tile-name { color: var(--brass-hi); font-weight: bold; }
.cp-empty { padding: 20px; text-align: center; opacity: 0.6; }
</style>
